import type { Messages } from './messages';

/** English copy. Schema and ordering follow the Korean tree in `messages.ts`. */
export const en: Messages = {
  meta: {
    documentTitle: 'Eco Terrarium: Micro Evolution — Conduct a Living Ecosystem',
  },
  common: {
    close: 'Close',
    cancel: 'Cancel',
  },
  units: {
    duration: (minutes: number, seconds: number) => `${minutes}m ${seconds < 10 ? '0' : ''}${seconds}s`,
    seconds: (value: number) => `${value}s`,
    organisms: (count: number) => `${count}`,
    totalOrganisms: (count: number) => `${count} total`,
    speciesCount: (count: number) => `${count} species`,
    generation: (value: number) => `Gen ${value}`,
  },
  language: {
    groupLabel: 'Language',
    optionLabel: (name: string) => name,
  },
  topbar: {
    title: 'Eco Terrarium',
    subtitle: 'Watch and tune the balance of life inside the jar',
    navLabel: 'Secondary menu',
    judgeShort: 'Tour',
    judgeFull: 'Judge Quick Tour',
    encyclopedia: 'Codex',
    encyclopediaTitle: 'Open the species codex',
    quests: 'Quests',
    questsTitle: 'Open the quest list',
    customization: 'Customize',
    customizationTitle: 'Customize the terrarium design',
    photo: 'Snapshot',
    photoTitle: 'Photo mode & snapshot',
    hive: 'Hive Share',
    hiveTitle: 'Com2uS Hive social share',
    qr: 'QR',
    qrTitle: 'Play on your phone (QR code)',
    helpTitle: 'How to play',
  },
  discovery: {
    kicker: 'New species recorded',
    action: 'Open codex',
  },
  dna: {
    defaultCreatorName: 'Ecosystem Conductor',
    defaultTerrariumName: 'My Eco Terrarium',
  },
  startScreen: {
    kicker: 'MICRO EVOLUTION SIMULATOR',
    title: 'Eco Terrarium',
    tagline: 'Conduct the balance of life inside a small glass jar.',
    description:
      'A calm ecosystem sim: tune light, moisture, temperature and nutrients to tend a food chain that runs from producers all the way up to predators.',
    objectiveTitle: 'Your goal',
    objectiveBody:
      'Keep any one species from taking over or dying out, and along the way discover new species and a higher Bio Harmony score.',
    playTitle: 'How to play',
    steps: [
      { label: 'Light & moisture', description: 'Build an environment where plants can grow.' },
      { label: 'Nutrients & catalyst', description: 'Drop food into the jar.' },
      { label: 'Temperature & balance', description: 'Watch the ecosystem adapt to change.' },
    ],
    footnote: 'Hunting and feeding show up as glowing particle bursts inside the jar.',
    action: 'Start the terrarium',
  },
  hud: {
    environmentSectionLabel: 'Environment controls',
    toolGroupLabel: 'Jar tool selection',
    tools: {
      inspect: {
        button: 'Microscope',
        buttonTitle: 'Click a microbe to observe it under the microscope and analyze its genome',
        guideTitle: 'Microscope lens',
        guideDetail: 'Click a glowing organism to inspect its name, generation, energy and genome.',
      },
      feed: {
        button: 'Feed',
        buttonTitle: 'Click inside the jar to drop organic nutrient pellets',
        guideTitle: 'Nutrient dropper',
        guideDetail: 'Drop one near an organism that is low on energy. Overfeeding breaks the balance.',
      },
      mutagen: {
        button: 'Mutagen',
        buttonTitle: 'Inject a mutagen to trigger rapid genetic variation',
        guideTitle: 'Mutagen catalyst',
        guideDetail: 'Use it only when chasing a specific evolution. Paired with heat, the change is far more dramatic.',
      },
      tap: {
        button: 'Tap jar',
        buttonTitle: 'Tap the glass to send out a shockwave',
        guideTitle: 'Tap the jar',
        guideDetail: 'A short shockwave lets you watch how organisms move. Tapping repeatedly stresses them.',
      },
    },
    dayNightLabel: 'Day / night',
    dayNightTitle: 'Toggle the automatic day/night cycle',
    speedGroupLabel: 'Simulation speed',
    play: 'Play simulation',
    pause: 'Pause simulation',
    playPauseTitle: 'Pause / play the simulation',
    unmute: 'Turn audio on',
    mute: 'Turn audio off',
    muteTitle: 'Mute the procedural soundtrack',
    microscopeHowToTitle: 'How to use the microscope',
    microscopeHowToCompact: 'Select an organism in the jar to see its details right away.',
    microscopeSteps: [
      'Select the “Microscope” tool.',
      'Click or tap a glowing organism inside the jar.',
      'Read its name, age, generation and genome in the inspector.',
    ],
    sliders: {
      sunlight: {
        label: 'Sunlight',
        aria: 'Sunlight',
        low: 'Dark',
        mid: 'Photosynthesis',
        high: 'Scorching',
      },
      moisture: {
        label: 'Moisture / Rain',
        aria: 'Moisture and rain',
        low: 'Dry',
        mid: 'Damp',
        high: 'Downpour',
      },
      temperature: {
        label: 'Temperature',
        aria: 'Temperature',
        low: '-10°C (ice age)',
        mid: '22°C (temperate)',
        high: '45°C (tropical)',
      },
      nutrients: {
        label: 'Nutrients',
        aria: 'Nutrients',
        low: 'Barren',
        mid: 'Fertile',
        high: 'Over-enriched',
      },
    },
  },
  canvas: {
    inspectAria: 'Microscope: click or tap an organism in the jar to open the inspector',
    defaultAria: 'Eco Terrarium jar',
    hints: {
      inspect: 'Click or tap an organism to open the inspector',
      feed: 'Select a spot in the jar to drop nutrients',
      mutagen: 'Select a spot in the jar to drop the mutagen',
      tap: 'Select the jar to create a shockwave',
    },
  },
  advisor: {
    sectionLabel: 'Ecosystem status and next step',
    title: 'Ecosystem advisor',
    subtitle: 'Reads health and the current environment together',
    healthAria: (health: number) => `Ecosystem health ${health}%`,
    readingsLabel: 'Environment summary',
    readingNames: {
      sunlight: 'Light',
      moisture: 'Moisture',
      temperature: 'Temp',
    },
    readings: {
      'sunlight.low': { label: 'Too dim', detail: 'Photosynthesis is slowing down' },
      'sunlight.high': { label: 'Harsh sunlight', detail: 'Watch for overheating and algae' },
      'sunlight.balanced': { label: 'Gentle light', detail: 'Producers are growing steadily' },
      'moisture.low': { label: 'Drought warning', detail: 'Water stress is rising' },
      'moisture.high': { label: 'Downpour', detail: 'Watch for saturation and murky water' },
      'moisture.balanced': { label: 'Healthy cycle', detail: 'Decomposition and aquatic life are active' },
      'temperature.cold': { label: 'Cold alert', detail: 'Frost forms and metabolism slows' },
      'temperature.hot': { label: 'Heat alert', detail: 'Heat and steam are straining the organisms' },
      'temperature.pressure': { label: 'Adaptive pressure', detail: 'Some organisms are under thermal stress' },
      'temperature.balanced': { label: 'Temperate', detail: 'Most organisms are comfortable' },
    },
    advice: {
      recover: {
        title: 'This needs recovery',
        detail: 'Bring light back to 45–70%, moisture to 50–80% and temperature to 18–26°C, then drop nutrients once.',
      },
      temperature: {
        title: 'Ease the temperature first',
        detail: 'Extreme temperatures burn energy fast. Unless you are chasing an adaptation, around 22°C is safe.',
      },
      'moisture.low': {
        title: 'Rebalance the moisture',
        detail: 'Let it rain and bring moisture above 50%.',
      },
      'moisture.high': {
        title: 'Rebalance the moisture',
        detail: 'Reduce rainfall below 80% to keep the water from turning murky.',
      },
      'sunlight.low': {
        title: 'Time to tune the light',
        detail: 'Raise the sunlight a little to feed the producers.',
      },
      'sunlight.high': {
        title: 'Time to tune the light',
        detail: 'Lower direct light to around 70% to avoid overheating.',
      },
      diversity: {
        title: 'Tend to the diversity',
        detail: 'Drop nutrients once, then watch whether all four trophic levels hold their numbers together.',
      },
      observe: {
        title: 'A good moment to observe',
        detail: 'The ecosystem is stable. Inspect generations and genomes, or try a small environmental experiment.',
      },
    },
  },
  stats: {
    sectionLabel: 'Ecosystem readings',
    health: 'Ecosystem health',
    harmony: 'Bio Harmony',
    foodWeb: 'Food web population',
    trophic: {
      producer: 'Producers',
      herbivore: 'Herbivores',
      predator: 'Predators',
      decomposer: 'Decomposers',
    },
    chartTitle: 'Lotka–Volterra dynamics',
    chartLegend: {
      producers: 'Plants',
      herbivores: 'Herb.',
      predators: 'Pred.',
    },
    chartEmpty: 'Collecting simulation data...',
    biodiversity: "Biodiversity H':",
    unlocked: 'Codex unlocked:',
    survival: 'Runtime:',
    extinction: 'Deaths / recycled:',
  },
  inspector: {
    dialogLabel: 'Organism inspector',
    unknownName: 'Microbe',
    unknownSpecies: 'Unidentified organism',
    saveName: 'Save name',
    editName: 'Edit name',
    energy: 'Energy / vitality',
    age: 'Age / lifespan',
    genomeTitle: 'Genome analysis (Genome DNA)',
    genomeDimensions: '10-dimensional phenotype vector',
    genome: {
      size: 'Size factor',
      speed: 'Swim speed',
      tempOpt: 'Optimal temperature',
      moistOpt: 'Optimal moisture',
      defense: 'Shell defense',
      mutationRate: 'Mutation rate',
      metabolism: 'Metabolism',
      hue: 'Body hue',
      bioluminescence: 'Bioluminescence',
    },
    injectMutagen: 'Inject mutagen into this organism',
  },
  encyclopedia: {
    dialogLabel: 'Species codex',
    title: 'Species Codex (Encyclopedia)',
    progress: (unlocked: number, total: number, percent: number) => `${unlocked} / ${total} found (${percent}%)`,
    subtitle: 'The evolutionary lineage and secrets of 16 microbes born inside a small glass jar',
    filters: {
      all: 'All',
      producer: 'Producers',
      herbivore: 'Herbivores',
      predator: 'Predators',
      decomposer: 'Decomposers',
    },
    lockedName: '??? Unidentified',
    lockedScientific: 'Evolves once conditions are met',
    lockedTitle: 'An unidentified species, not yet discovered',
    lockedBody: 'Tune the terrarium environment to meet the conditions for evolution.',
    loreTitle: 'Field notes (Lore)',
    traitsTitle: 'Innate traits',
    traits: {
      tempOpt: 'Optimal temp',
      moistOpt: 'Optimal moisture',
      speed: 'Movement speed',
      bioluminescence: 'Bioluminescence',
    },
    hintTitle: 'Evolution hint',
  },
  quests: {
    dialogLabel: 'Ecosystem conductor quests',
    title: 'Ecosystem Conductor Quests',
    progress: (completed: number, total: number, percent: number) => `${completed} / ${total} done (${percent}%)`,
    subtitle: 'Complete objectives to unlock exclusive terrarium parts and reagents',
    reward: (title: string) => `Reward: ${title}`,
    completed: 'Complete',
    inProgress: 'In progress',
  },
  customization: {
    dialogLabel: 'Terrarium design customization',
    title: 'Terrarium Customization',
    subtitle: 'Decorate the jar shape, the substrate and the background as you like',
    bottleTitle: 'Bottle shape',
    substrateTitle: 'Substrate theme',
    backgroundTitle: 'Background theme',
    bottles: {
      'classic-jar': { name: 'Classic jar', desc: 'The default cylinder with soft curves' },
      'geometric-dome': { name: 'Geometric dome', desc: 'A diamond-cut glass dome' },
      'antique-flask': { name: 'Antique flask', desc: 'A mysterious alchemist round flask' },
      'crystal-sphere': { name: 'Crystal sphere', desc: 'A dreamlike orb floating in the air' },
    },
    substrates: {
      'moss-forest': { name: 'Moss Forest', desc: 'Lush green moss and rich soil' },
      'deep-sea-sand': { name: 'Deep Sea', desc: 'Seabed sand glowing in blue' },
      'volcanic-obsidian': { name: 'Volcanic', desc: 'Obsidian bedrock still holding magma heat' },
      'crystal-cave': { name: 'Crystal Cave', desc: 'Rocky ground sprouting violet crystals' },
    },
    backgrounds: {
      'cozy-lab': { name: 'Cozy lab', desc: 'A warm study with soft lighting' },
      'dawn-mist': { name: 'Dawn mist forest', desc: 'Crisp forest air and morning sun' },
      'sunset-window': { name: 'Sunset window', desc: 'The red glow of dusk' },
      'cosmic-aurora': { name: 'Cosmic aurora', desc: 'Auroras dancing with the Milky Way' },
    },
  },
  photo: {
    dialogLabel: 'Photo mode and snapshot',
    title: 'Photo Mode & Snapshot',
    subtitle: 'Keep a beautiful moment of your terrarium with a mood filter',
    loading: 'Loading capture...',
    filtersTitle: 'Mood filters',
    filters: {
      original: 'Natural',
      vintage: 'Vintage film',
      neon: 'Cyber neon',
      pastel: 'Dream pastel',
      noir: 'Classic noir',
    },
    download: 'Save high-resolution snapshot (PNG)',
  },
  help: {
    dialogLabel: 'Eco Terrarium play guide',
    title: 'Eco Terrarium — How to Play',
    subtitle: 'How to become the conductor of a jar-sized ecosystem',
    loopTitle: '1. The four rings of the ecosystem (Core Loop)',
    loopBody: 'Four levels of life coexist inside the jar, endlessly cycling matter and energy:',
    loopRoles: {
      producer: 'Photosynthesis',
      herbivore: 'Graze & excrete',
      predator: 'Hunt herbivores',
      decomposer: 'Carcasses to nutrients',
    },
    toolsTitle: '2. Environment controls and god tools',
    tools: {
      sunlight: { name: 'Sunlight', desc: 'Boosts photosynthesis and nudges the temperature up.' },
      moisture: { name: 'Moisture / rain', desc: 'Droplets form, activating aquatic life and decomposers.' },
      temperature: { name: 'Temperature', desc: 'Applies mutation pressure from an ice age (-10°C) to a heatwave (45°C).' },
      tap: { name: 'Tap the jar', desc: 'Knock on the glass to scatter clustered organisms.' },
    },
    speciationTitle: '3. A 16-species codex & procedural bio-sound',
    speciationBody:
      'Extreme environmental swings or a mutagen injection give birth to 16 distinctive new species. The more diverse the ecosystem, the richer the bio-harmony soundtrack the Web Audio API generates in real time!',
    saveTitle: '4. Autosave & reset',
    saveBody:
      'Your terrarium and quest progress are saved automatically in this browser, so you can close the window and pick up where you left off. To start over, reset below — codex unlocks and quest records are cleared too.',
    resetWarning: 'Reset for real? The current ecosystem cannot be recovered.',
    resetConfirm: 'Reset',
    resetTrigger: 'Start the terrarium over',
  },
  hive: {
    dialogLabel: 'Hive share and visit',
    title: 'Com2uS Hive Social Platform',
    subtitle: 'Share a terrarium DNA code and visit another conductor’s ecosystem',
    tabs: {
      share: 'Share mine',
      import: 'Load a code',
      visitor: 'Visitor mode',
      leaderboard: 'Global ranking',
    },
    referenceId: 'Terrarium reference ID',
    dnaValid: 'Ecosystem DNA valid',
    copyCode: 'Copy reference ID',
    copyLink: 'Copy link',
    copied: 'Copied!',
    referenceNote:
      'The reference ID is a fingerprint for telling links apart. To restore a whole ecosystem, share the full DNA link below.',
    deepLink: 'One-click deep link (Direct Web Link)',
    snapshot: {
      discovered: 'Species found',
      harmony: 'Harmony score',
      population: 'Population',
    },
    importIntro:
      'Paste a full DNA link or a compressed DNA string that a friend shared, and the environment, codex unlocks, population and gene pool are all restored together.',
    importPlaceholder: 'Paste a full DNA link or compressed DNA string...',
    importEmptyError: 'Enter a code or a URL.',
    importInvalidError: 'That is not a valid terrarium DNA code. Please check it again.',
    importAction: 'Load this terrarium',
    visitor: {
      featured: 'Featured terrarium: Cat’s Moss Forest',
      featuredOwner: 'Conductor: HIDEA_Kim (official Cat & Soup theme)',
      giftPollen: 'Gift pollen',
      giftPollenDone: 'Gift sent!',
      giftPollenDesc: 'Grants the host a nutrient buff',
      harvestSpore: 'Harvest Aurora Fin spores',
      harvestSporeDone: 'Harvested!',
      harvestSporeDesc: 'Transplant into your terrarium',
    },
    leaderboard: [
      { playerName: 'Conductor_Michelle', terrariumName: 'Aurora Nebula Garden', badge: 'Grand Maestro' },
      { playerName: 'CatAndSoupFan', terrariumName: 'Squishy Jelly Bottle', badge: 'Healing Guardian' },
      { playerName: 'MintRocket_Dave', terrariumName: 'Deep-Sea Kraken Biome', badge: 'Deep-Sea Explorer' },
      { playerName: 'Bio_Codex', terrariumName: 'Cryogenic Crystal Dome', badge: 'Ice Age Pioneer' },
    ],
  },
  qr: {
    dialogLabel: 'Play on your phone',
    title: 'Play on your phone',
    subtitle: 'Point your camera at the QR code — it opens in the browser, no install needed',
    scanHint: 'Just point the stock iPhone or Android camera at it',
    urlLabel: 'Play link',
    copyLink: 'Copy link',
    copied: 'Copied!',
    localNotice: 'This screen is running on a local address that phones cannot reach, so the QR code points to the public link.',
  },
  showcase: {
    dialogLabel: 'Judges-only showcase',
    title: 'Judges-Only Showcase & Codex Dev Story',
    subtitle: 'One-minute quick-tour presets and the OpenAI Codex collaboration architecture',
    tabPresets: 'One-minute quick presets',
    tabCodex: 'OpenAI Codex dev story',
    presetApplied: (name: string) => `“${name}” preset applied.`,
    applyPreset: 'Apply preset',
    presets: {
      prosperity: {
        name: 'Instant Prosperity',
        heading: '01. Instant Prosperity (Full Prosperity)',
        caption: 'Stage the ecosystem at its peak',
        body: 'Instantly builds an optimal ecosystem where all four trophic levels (plants, herbivores, predators, decomposers) drift in perfect balance.',
      },
      mutation: {
        name: 'Mutation Burst',
        heading: 'Mutation Burst',
        caption: 'Trigger a speciation event',
        body: 'Raises the temperature to 35°C and injects a heavy dose of mutagen, demonstrating rare variants such as Aurora Fin and Glow Tail within five seconds.',
      },
      iceage: {
        name: 'Ice Age Escape',
        heading: 'Ice Age Escape',
        caption: 'Cryogenic adaptation simulation',
        body: 'Crashes the temperature to -5°C so frost forms on the glass, and follows Crystal Leaf and other cold-tolerant species adapting to the extreme.',
      },
      symphony: {
        name: 'Harmony Orchestra',
        heading: 'Harmony Orchestra (Full Symphony)',
        caption: 'Listen to the Web Audio soundscape',
        body: 'Sets ecosystem health to 100% so ambient pads, pentatonic bio-chimes and rain polyphony layer into the fullest chord the sim can produce.',
      },
    },
    codex: {
      whereTitle: '1. Where Codex was used',
      whereItems: [
        {
          term: 'Mathematical ecosystem model',
          body: 'Designed the numerical solver that integrates the four coupled Lotka–Volterra equations with fourth-order Runge–Kutta (RK4).',
        },
        {
          term: 'Procedural sound synth',
          body: 'A pentatonic adaptive-harmony generator built purely in Web Audio API code, with no external MP3 assets.',
        },
        {
          term: 'Genetic fluid simulation',
          body: 'Drift across the 10-dimensional phenotype vector, plus Boids flocking optimization.',
        },
      ],
      problemTitle: '2. Solving technical problems with Codex',
      problemItems: [
        {
          term: 'Taming divergence in the ODEs',
          body: 'Stabilized the nonlinear oscillation — populations collapsing to zero or exploding without bound — by introducing an Environmental Buffer Multiplier.',
        },
        {
          term: '60 FPS garbage-collection tuning',
          body: 'Removed the GC spikes caused by spawning hundreds of particles per frame, using entity pooling and batched canvas rendering.',
        },
      ],
      decisionTitle: '3. The human creator’s artistic and design calls',
      decisionBody:
        'Rather than settling for a simulation that just prints numbers, this combines the warm healing interaction of Donggyu Kim’s ‘Cat & Soup’ with the fresh sandbox discovery of Daehwon Kim’s ‘MINTROCKET’, turning it into an original piece of work.',
    },
  },
};
